import { z } from 'zod';
import * as Sentry from '@sentry/nextjs';

/**
 * Environment variable schema.
 *
 * Validates all environment variables consumed by the application config at
 * module-load time so that missing or malformed values surface immediately with
 * a clear, actionable error message rather than causing cryptic failures deep
 * in component trees.
 *
 * Each variable is referenced individually via process.env.VAR so that Next.js
 * webpack DefinePlugin can inline NEXT_PUBLIC_* values into client bundles.
 * Passing `process.env` as a whole object does NOT work client-side.
 */
const envSchema = z.object({
  NEXT_PUBLIC_CANONICAL_URL: z.string().url(),
  NEXT_PUBLIC_SANITY_PROJECT_ID: z.string().min(1),
  NEXT_PUBLIC_SANITY_DATASET: z.string().min(1),
  NEXT_PUBLIC_S3_UPLOAD_BUCKET: z.string().min(1),
  NEXT_PUBLIC_APPLE_CLIENT_ID: z.string().min(1),
  NEXT_PUBLIC_FACEBOOK_APP_ID: z.string().min(1),
  NEXT_PUBLIC_GOOGLE_API_KEY: z.string().min(1),
  NEXT_PUBLIC_GOOGLE_CLIENT_ID: z.string().min(1),
  NEXT_PUBLIC_HOSTFULLY_BASE_URL: z.string().url().optional(),
  NEXT_PUBLIC_HOSTFULLY_OAUTH_CALLBACK_BASE_URL: z.string().url().optional(),
  NEXT_PUBLIC_HOSTFULLY_CLIENT_ID: z.string().min(1).optional(),
  NEXT_PUBLIC_HOSPITABLE_CLIENT_ID: z.string().min(1).optional(),
  NEXT_PUBLIC_IMAGEKIT_ENDPOINT: z.string().url(),
  NEXT_PUBLIC_STRIPE_GIFT_CARD_PUBLIC_KEY: z.string().startsWith('pk_'),
  NEXT_PUBLIC_STRIPE_PUBLIC_KEY: z.string().startsWith('pk_'),
  NEXT_PUBLIC_STRIPE_FREE_MONTHLY_PLAN_ID: z.string().min(1),
  NEXT_PUBLIC_STRIPE_BRONZE_MONTHLY_PLAN_ID: z.string().min(1),
  NEXT_PUBLIC_STRIPE_SILVER_MONTHLY_PLAN_ID: z.string().min(1),
  NEXT_PUBLIC_STRIPE_GOLD_MONTHLY_PLAN_ID: z.string().min(1),
  NEXT_PUBLIC_STRIPE_PRIVATE_MONTHLY_PLAN_ID: z.string().min(1),
  NEXT_PUBLIC_STRIPE_PAY_PER_RESERVATION_PROMO_CODE: z.string().min(1),
  // Optional during the BHT-7146 log-only rollout; the signup captcha widget
  // renders only when the site key is present.
  NEXT_PUBLIC_TURNSTILE_SITE_KEY: z.string().min(1).optional(),
  LISTING_ENGINE_GRAPH_URL: z.string().url().optional(),
  // Shared with listing_engine GRAPHQL_PROXY_SHARED_SECRET so Graph can trust
  // the browser client IP attested by this Next.js proxy (BHT-7147 follow-up).
  GRAPHQL_PROXY_SHARED_SECRET: z.string().min(1).optional(),
  NEXT_PUBLIC_SANITY_API_VERSION: z.string().default('2024-01-01'),
  FACEBOOK_ACCESS_TOKEN: z.string().optional(),
  SANITY_API_READ_TOKEN: z.string().min(1).optional(),
});

function collectEnv(): Record<string, string | undefined> {
  return {
    NEXT_PUBLIC_CANONICAL_URL: process.env.NEXT_PUBLIC_CANONICAL_URL,
    NEXT_PUBLIC_SANITY_PROJECT_ID: process.env.NEXT_PUBLIC_SANITY_PROJECT_ID,
    NEXT_PUBLIC_SANITY_DATASET: process.env.NEXT_PUBLIC_SANITY_DATASET,
    NEXT_PUBLIC_S3_UPLOAD_BUCKET: process.env.NEXT_PUBLIC_S3_UPLOAD_BUCKET,
    NEXT_PUBLIC_APPLE_CLIENT_ID: process.env.NEXT_PUBLIC_APPLE_CLIENT_ID,
    NEXT_PUBLIC_FACEBOOK_APP_ID: process.env.NEXT_PUBLIC_FACEBOOK_APP_ID,
    NEXT_PUBLIC_GOOGLE_API_KEY: process.env.NEXT_PUBLIC_GOOGLE_API_KEY,
    NEXT_PUBLIC_GOOGLE_CLIENT_ID: process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID,
    NEXT_PUBLIC_HOSTFULLY_BASE_URL: process.env.NEXT_PUBLIC_HOSTFULLY_BASE_URL,
    NEXT_PUBLIC_HOSTFULLY_OAUTH_CALLBACK_BASE_URL:
      process.env.NEXT_PUBLIC_HOSTFULLY_OAUTH_CALLBACK_BASE_URL,
    NEXT_PUBLIC_HOSTFULLY_CLIENT_ID:
      process.env.NEXT_PUBLIC_HOSTFULLY_CLIENT_ID,
    NEXT_PUBLIC_HOSPITABLE_CLIENT_ID:
      process.env.NEXT_PUBLIC_HOSPITABLE_CLIENT_ID,
    NEXT_PUBLIC_IMAGEKIT_ENDPOINT: process.env.NEXT_PUBLIC_IMAGEKIT_ENDPOINT,
    NEXT_PUBLIC_STRIPE_GIFT_CARD_PUBLIC_KEY:
      process.env.NEXT_PUBLIC_STRIPE_GIFT_CARD_PUBLIC_KEY,
    NEXT_PUBLIC_STRIPE_PUBLIC_KEY: process.env.NEXT_PUBLIC_STRIPE_PUBLIC_KEY,
    NEXT_PUBLIC_STRIPE_FREE_MONTHLY_PLAN_ID:
      process.env.NEXT_PUBLIC_STRIPE_FREE_MONTHLY_PLAN_ID,
    NEXT_PUBLIC_STRIPE_BRONZE_MONTHLY_PLAN_ID:
      process.env.NEXT_PUBLIC_STRIPE_BRONZE_MONTHLY_PLAN_ID,
    NEXT_PUBLIC_STRIPE_SILVER_MONTHLY_PLAN_ID:
      process.env.NEXT_PUBLIC_STRIPE_SILVER_MONTHLY_PLAN_ID,
    NEXT_PUBLIC_STRIPE_GOLD_MONTHLY_PLAN_ID:
      process.env.NEXT_PUBLIC_STRIPE_GOLD_MONTHLY_PLAN_ID,
    NEXT_PUBLIC_STRIPE_PRIVATE_MONTHLY_PLAN_ID:
      process.env.NEXT_PUBLIC_STRIPE_PRIVATE_MONTHLY_PLAN_ID,
    NEXT_PUBLIC_STRIPE_PAY_PER_RESERVATION_PROMO_CODE:
      process.env.NEXT_PUBLIC_STRIPE_PAY_PER_RESERVATION_PROMO_CODE,
    NEXT_PUBLIC_TURNSTILE_SITE_KEY:
      process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY,
    NEXT_PUBLIC_SANITY_API_VERSION: process.env.NEXT_PUBLIC_SANITY_API_VERSION,
    ...(typeof window === 'undefined'
      ? {
          LISTING_ENGINE_GRAPH_URL: process.env.LISTING_ENGINE_GRAPH_URL,
          GRAPHQL_PROXY_SHARED_SECRET:
            process.env.GRAPHQL_PROXY_SHARED_SECRET,
          FACEBOOK_ACCESS_TOKEN: process.env.FACEBOOK_ACCESS_TOKEN,
          SANITY_API_READ_TOKEN: process.env.SANITY_API_READ_TOKEN,
        }
      : {}),
  };
}

const testEnvSchema = envSchema.partial().extend({
  NEXT_PUBLIC_SANITY_API_VERSION: z.string().default('2024-01-01'),
});

function validateEnv() {
  const raw = collectEnv();
  const result = envSchema.safeParse(raw);
  if (!result.success) {
    // Test environment: graceful fallback so tests don't need every env var
    if (process.env.NODE_ENV === 'test') {
      const testResult = testEnvSchema.safeParse(raw);
      return (testResult.success ? testResult.data : { ...raw }) as z.infer<
        typeof envSchema
      >;
    }

    const { fieldErrors } = result.error.flatten();
    console.error(
      'Invalid environment variables:',
      JSON.stringify(fieldErrors, null, 2)
    );

    // Development: throw hard for immediate, clear feedback
    if (process.env.NODE_ENV === 'development') {
      throw new Error(
        `Environment validation failed:\n${Object.entries(fieldErrors)
          .map(([key, errors]) => `  ${key}: ${errors?.join(', ')}`)
          .join('\n')}`
      );
    }

    // Production: report to Sentry but do NOT throw — the app continues
    // with best-effort config so that a single misconfigured variable
    // doesn't take down every page and API route.
    Sentry.captureException(new Error('Environment validation failed'), {
      extra: { fieldErrors },
    });
    return raw as z.infer<typeof envSchema>;
  }
  return result.data;
}

const env = validateEnv();

function getConfig() {
  return {
    serverRuntimeConfig: {
      graphqlURL: env.LISTING_ENGINE_GRAPH_URL
        ? new URL(env.LISTING_ENGINE_GRAPH_URL)
        : undefined,
      graphqlProxySharedSecret: env.GRAPHQL_PROXY_SHARED_SECRET,
      facebook: {
        accessToken: env.FACEBOOK_ACCESS_TOKEN,
      },
      sanity: {
        token: env.SANITY_API_READ_TOKEN,
      },
    },
    publicRuntimeConfig: {
      canonicalUrl: (() => {
        try {
          return new URL(env.NEXT_PUBLIC_CANONICAL_URL);
        } catch {
          return new URL('https://www.savvy.com');
        }
      })(),
      sanity: {
        projectId: env.NEXT_PUBLIC_SANITY_PROJECT_ID,
        dataset: env.NEXT_PUBLIC_SANITY_DATASET,
        apiVersion: env.NEXT_PUBLIC_SANITY_API_VERSION,
      },
      aws: {
        uploadBucket: env.NEXT_PUBLIC_S3_UPLOAD_BUCKET,
      },
      apple: {
        clientId: env.NEXT_PUBLIC_APPLE_CLIENT_ID,
      },
      facebook: {
        appId: env.NEXT_PUBLIC_FACEBOOK_APP_ID,
      },
      google: {
        apiKey: env.NEXT_PUBLIC_GOOGLE_API_KEY,
        clientId: env.NEXT_PUBLIC_GOOGLE_CLIENT_ID,
      },
      hostfully: {
        baseUrl: env.NEXT_PUBLIC_HOSTFULLY_BASE_URL,
        oauthCallbackBaseUrl: env.NEXT_PUBLIC_HOSTFULLY_OAUTH_CALLBACK_BASE_URL,
        clientId: env.NEXT_PUBLIC_HOSTFULLY_CLIENT_ID,
      },
      hospitable: {
        clientId: env.NEXT_PUBLIC_HOSPITABLE_CLIENT_ID,
      },
      imagekitEndpoint: env.NEXT_PUBLIC_IMAGEKIT_ENDPOINT,
      graphqlProxyPath: '/api/graphql/',
      stripe: {
        giftCardPublicKey: env.NEXT_PUBLIC_STRIPE_GIFT_CARD_PUBLIC_KEY,
        publicKey: env.NEXT_PUBLIC_STRIPE_PUBLIC_KEY,
        subscriptionProducts: {
          freeMonthly: {
            id: env.NEXT_PUBLIC_STRIPE_FREE_MONTHLY_PLAN_ID,
          },
          bronzeMonthly: {
            id: env.NEXT_PUBLIC_STRIPE_BRONZE_MONTHLY_PLAN_ID,
          },
          silverMonthly: {
            id: env.NEXT_PUBLIC_STRIPE_SILVER_MONTHLY_PLAN_ID,
          },
          goldMonthly: {
            id: env.NEXT_PUBLIC_STRIPE_GOLD_MONTHLY_PLAN_ID,
          },
          privateMonthly: {
            id: env.NEXT_PUBLIC_STRIPE_PRIVATE_MONTHLY_PLAN_ID,
          },
        },
        payPerReservationPromoCode:
          env.NEXT_PUBLIC_STRIPE_PAY_PER_RESERVATION_PROMO_CODE,
      },
      turnstile: {
        siteKey: env.NEXT_PUBLIC_TURNSTILE_SITE_KEY,
      },
    },
  };
}

export default getConfig;
